/**
 * seatPicks — the four maps of "Teach us your seat" and what the picks teach. Pure.
 * Register SF1. Every map here is a drawn layout, labelled as one on screen. A real
 * snapshot takes pick 1 when we have read a flight the owner holds.
 */
import type { RealFlightMap, SeatCell, SeatLayout, SeatPosition, SeatPrefs } from "./seatTypes";

export interface PickMap {
  id: string;
  prompt: string;
  /** Plain line saying what this map is. A sample never reads as a real flight. */
  note: string;
  real: boolean;
  keepLooking: boolean;
  seats: SeatCell[];
  layout: SeatLayout;
}

export interface SeatPick {
  mapId: string;
  real: boolean;
  /** Null: they would rather keep looking. */
  seat: string | null;
  row: number | null;
  position: SeatPosition | null;
  exitRow: boolean;
  bulkhead: boolean;
  nearToilet: boolean;
  side: "left" | "right" | null;
  /** The map offered these, so not taking them says something. */
  offered: { exitRow: boolean; bulkhead: boolean };
}

/** One row as a string: x taken, o open, m Main Cabin Extra, p Preferred, | the aisle. */
interface RowSpec {
  row: number;
  cells: string;
  exit?: boolean;
  bulkhead?: boolean;
  toilet?: boolean;
}

function buildMap(letters: string[], specs: RowSpec[]): { seats: SeatCell[]; layout: SeatLayout } {
  const leftCount = Math.max(...specs.map((s) => s.cells.indexOf("|")));
  const seats: SeatCell[] = [];
  for (const spec of specs) {
    const chars = spec.cells.replace("|", "").split("");
    chars.forEach((c, i) => {
      if (c === " ") return;
      const letter = letters[i];
      const position: SeatPosition = i === 0 || i === letters.length - 1 ? "window" : i === leftCount - 1 || i === leftCount ? "aisle" : "middle";
      seats.push({
        seat: `${spec.row}${letter}`,
        row: spec.row,
        letter,
        cabin: "Main Cabin",
        available: c !== "x",
        position,
        exitRow: !!spec.exit,
        wingRow: false,
        limitedRecline: false,
        bulkhead: !!spec.bulkhead,
        nearToilet: !!spec.toilet,
        paid: c === "m" ? "main_cabin_extra" : c === "p" ? "preferred" : null,
        letterCertain: true,
      });
    });
  }
  const rows = specs.map((s) => s.row);
  return {
    seats,
    layout: { cabins: [{ name: "Main Cabin", letters, aisleAfter: [leftCount - 1], firstRow: Math.min(...rows), lastRow: Math.max(...rows) }] },
  };
}

const SIX = ["A", "B", "C", "D", "E", "F"];
const SAMPLE = "A sample layout, not a real flight.";

const PLENTY = buildMap(SIX, [
  { row: 8, cells: "xxm|mxx", bulkhead: true },
  { row: 9, cells: "xxm|xmm" },
  { row: 10, cells: "mxx|mxx" },
  { row: 13, cells: "pxx|xpp" },
  { row: 14, cells: "xpp|pxp" },
  { row: 15, cells: "ppx|xpp" },
  { row: 16, cells: "mmx|xmm", exit: true },
  { row: 17, cells: "mmm|mmx", exit: true },
  { row: 18, cells: "oxo|oox" },
  { row: 19, cells: "ooo|xoo" },
  { row: 20, cells: "oxo|ooo" },
  { row: 21, cells: "xoo|oxo" },
  { row: 22, cells: "ooo|ooo" },
]);

const BULKHEAD = buildMap(SIX, [
  { row: 7, cells: "mmm|mmm", bulkhead: true },
  { row: 8, cells: "xmx|xxm" },
  { row: 9, cells: "mxx|oxx" },
  { row: 10, cells: "xxo|xxo" },
  { row: 11, cells: "oxx|xxo" },
  { row: 12, cells: "xxo|oxx" },
  { row: 13, cells: "oxo|xxo" },
  { row: 14, cells: "xoo|ooo" },
]);

const NEARLY_FULL = buildMap(SIX, [
  { row: 15, cells: "xxx|xxx" },
  { row: 16, cells: "xxx|xxx" },
  { row: 17, cells: "xxx|xxx", exit: true },
  { row: 18, cells: "xxx|xxx", exit: true },
  { row: 19, cells: "xxo|xxx" },
  { row: 20, cells: "oxx|xxx" },
  { row: 21, cells: "xxx|xxx" },
  { row: 22, cells: "xox|xxx" },
  { row: 23, cells: "xxx|xox" },
  { row: 24, cells: "oxx|xxx" },
  { row: 25, cells: "xxx|xxx" },
  { row: 26, cells: "xxx|oxx", toilet: true },
]);

const REGIONAL = buildMap(["A", "C", "D", "F"], [
  { row: 7, cells: "mm|mm", bulkhead: true },
  { row: 8, cells: "xm|mx", exit: true },
  { row: 9, cells: "ox|xo" },
  { row: 10, cells: "oo|xo" },
  { row: 11, cells: "xo|oo" },
  { row: 12, cells: "ox|oo" },
  { row: 13, cells: "oo|ox" },
  { row: 14, cells: "ox|xo", toilet: true },
]);

/** The main cabin of a real read. Null when it has nothing to pick from. */
export function realPickMap(real: RealFlightMap): PickMap | null {
  const cabin = real.layout.cabins.find((c) => !/first|business/i.test(c.name));
  if (!cabin) return null;
  const seats = real.seats.filter((s) => s.cabin === cabin.name && s.letterCertain);
  if (!seats.some((s) => s.available)) return null;
  return {
    id: "real",
    prompt: "This is your real flight. Tap the seat you would take.",
    note: `This is your real flight: ${real.label}.`,
    real: true,
    keepLooking: false,
    seats,
    layout: { cabins: [cabin] },
  };
}

export function pickMaps(real: RealFlightMap | null): PickMap[] {
  const realMap = real ? realPickMap(real) : null;
  return [
    realMap ?? { id: "plenty", prompt: "Plenty is open. Tap the seat you would take.", note: SAMPLE, real: false, keepLooking: false, ...PLENTY },
    { id: "bulkhead", prompt: "A different plane, and the front wall row is open. Tap the seat you would take.", note: SAMPLE, real: false, keepLooking: false, ...BULKHEAD },
    { id: "nearly-full", prompt: "Almost full. Which would you take, or would you keep looking?", note: SAMPLE, real: false, keepLooking: true, ...NEARLY_FULL },
    { id: "regional", prompt: "A small plane with two seats each side. Tap the seat you would take.", note: SAMPLE, real: false, keepLooking: false, ...REGIONAL },
  ];
}

export function sideOf(seat: SeatCell, layout: SeatLayout): "left" | "right" | null {
  const cabin = layout.cabins.find((c) => c.name === seat.cabin);
  if (!cabin) return null;
  return cabin.letters.indexOf(seat.letter) < cabin.letters.length / 2 ? "left" : "right";
}

export function pickOf(map: PickMap, seat: SeatCell | null): SeatPick {
  return {
    mapId: map.id,
    real: map.real,
    seat: seat?.seat ?? null,
    row: seat?.row ?? null,
    position: seat?.position ?? null,
    exitRow: seat?.exitRow ?? false,
    bulkhead: seat?.bulkhead ?? false,
    nearToilet: seat?.nearToilet === true,
    side: seat ? sideOf(seat, map.layout) : null,
    offered: {
      exitRow: map.seats.some((s) => s.available && s.exitRow),
      bulkhead: map.seats.some((s) => s.available && s.bulkhead),
    },
  };
}

export const EMPTY_PREFS: SeatPrefs = {
  seatTypeOrder: [],
  exitRow: null,
  bulkhead: null,
  aheadOfRow: null,
  avoidToilets: null,
  side: null,
  companionRule: null,
  worstAccept: null,
  payLimit: 0,
  moveADay: null,
};

/** What the picks teach. A starting point: every line can be edited on the summary. */
export function deriveFromPicks(picks: SeatPick[]): SeatPrefs {
  const made = picks.filter((p) => p.seat);
  const counts: Record<SeatPosition, number> = { aisle: 0, window: 0, middle: 0 };
  // The first pick breaks a tie, so the order starts with what they took first.
  made.forEach((p, i) => { if (p.position) counts[p.position] += made.length - i / 10; });
  const order = (["aisle", "window", "middle"] as SeatPosition[]).sort((a, b) => counts[b] - counts[a]);

  const exitOffered = made.filter((p) => p.offered.exitRow);
  const exitRow = exitOffered.some((p) => p.exitRow) ? true : null;
  const bulkOffered = picks.filter((p) => p.offered.bulkhead);
  const bulkhead = bulkOffered.some((p) => p.bulkhead) ? "want" : null;

  const rows = made.filter((p) => p.row != null).map((p) => p.row as number);
  const aheadOfRow = rows.length ? Math.max(...rows) + 3 : null;
  const sides = new Set(made.map((p) => p.side));
  const side = made.length >= 3 && sides.size === 1 ? (made[0].side as SeatPrefs["side"]) : null;

  return {
    ...EMPTY_PREFS,
    seatTypeOrder: made.length ? order : [],
    exitRow,
    bulkhead,
    aheadOfRow,
    avoidToilets: made.some((p) => p.nearToilet) ? null : true,
    side,
  };
}

/** The skip path's one question. */
export function simplePrefs(answer: "aisle" | "window" | "either"): SeatPrefs {
  const order: SeatPosition[] = answer === "window" ? ["window", "aisle", "middle"] : ["aisle", "window", "middle"];
  return { ...EMPTY_PREFS, seatTypeOrder: answer === "either" ? ["aisle", "window"] : order, worstAccept: answer === "either" ? "window" : null };
}

export function ariaForSeat(s: SeatCell): string {
  const bits = [`Row ${s.row} seat ${s.letter}`];
  if (s.exitRow) bits.push("exit row");
  if (s.position) bits.push(s.position);
  if (s.bulkhead) bits.push("bulkhead");
  if (s.nearToilet) bits.push("by the toilets");
  if (s.paid === "main_cabin_extra") bits.push("Main Cabin Extra");
  if (s.paid === "preferred") bits.push("Preferred");
  if (!s.available) bits.push("taken");
  return bits.join(", ");
}

/** The summary's wording for each line. */
export function orderLabel(order: SeatPosition[]): string {
  if (order.length === 0) return "Not told";
  const [a, b] = order;
  const cap = a.charAt(0).toUpperCase() + a.slice(1);
  return b ? `${cap}, then ${b}` : cap;
}

export function payLabel(limit: number | null): string {
  if (limit === null) return "No limit";
  return limit === 0 ? "$0" : `Up to $${limit}`;
}

export function savedSummary(p: SeatPrefs): string {
  const bits = [orderLabel(p.seatTypeOrder)];
  if (p.exitRow === true) bits.push("exit row first");
  if (p.aheadOfRow != null) bits.push(`ahead of row ${p.aheadOfRow}`);
  bits.push(`${payLabel(p.payLimit)} each way`);
  return bits.join(", ") + ".";
}
